/**
 * Everything the team can say out loud. Each line is recorded once per voice
 * (public/voces/<voz>/<id>.mp3), so the wording here is fixed: numbers, names
 * and dates never go inside a line, they travel beside it as figures on the
 * screen. Change a sentence and `recorded.test.ts` fails until the clip is
 * recorded again with the same text.
 */
export const LINES = {
  // Júpiter opens the stage.
  'saludo-manana-pendientes': 'Buenos días. Tu equipo dejó trabajo listo para revisar. Empieza por lo primero de la lista.',
  'saludo-tarde-pendientes': 'Buenas tardes. Tu equipo dejó trabajo listo para revisar. Empieza por lo primero de la lista.',
  'saludo-noche-pendientes': 'Buenas noches. Tu equipo dejó trabajo listo para revisar. Empieza por lo primero de la lista.',
  'saludo-manana-aldia': 'Buenos días. Nada que revisar por ahora: tu equipo está al día.',
  'saludo-tarde-aldia': 'Buenas tardes. Nada que revisar por ahora: tu equipo está al día.',
  'saludo-noche-aldia': 'Buenas noches. Nada que revisar por ahora: tu equipo está al día.',
  'primero-pendiente': 'Empieza por lo primero de la lista. Es lo que más te conviene revisar ahora.',
  'primero-aldia': 'Todo al día. Si algo cambia en tus números, tu equipo te lo prepara aquí.',
  puedo:
    'Puedo preparar una oferta para un día flojo, escribirle a quien no regresa, confirmar las citas de mañana y explicarte cómo van tus finanzas.',
  nosabe:
    'Eso todavía no lo sé hacer. Pregúntame por ofertas, por clientes que no regresan, por las citas de mañana o por tus finanzas.',

  // The work already prepared.
  'winback-handoff': 'Hay clientes que hace rato no regresan. Les preparé un mensaje; revísalo y lo envías tú desde tu WhatsApp.',
  'confirm-handoff': 'Mañana tienes citas por confirmar. Preparé el mensaje; revísalo y lo envías tú.',
  'offer-handoff': 'Hay un día flojo que podemos llenar. Te dejé la oferta lista; revisa el descuento y las fechas, y apruébala.',
  'winback-nada': 'Nadie se está quedando atrás: tus clientes siguen viniendo.',
  'confirm-nada': 'Mañana no hay citas por confirmar con número de WhatsApp.',
  'offer-nada': 'Ningún día está flojo ahora mismo, así que no preparé ofertas.',

  // Mercurio, the numbers of the month.
  'resumen-mes': 'Así va tu negocio este mes. Te dejo los números aquí abajo.',
  'ventas-mes': 'Esto es lo que ha entrado este mes. Te dejo los números aquí abajo.',
  'ventas-nada': 'Este mes todavía no hay ventas registradas. Cuando cobres tu primera visita, la verás aquí.',
  'hoy-cobrado': 'Hoy ya entró dinero. Aquí abajo tienes el total del día.',
  'hoy-nada': 'Hoy todavía no has cobrado nada. Cuando entre el primer pago, te lo cuento.',
  'ayer-cobrado': 'Ayer entró dinero. Aquí abajo tienes el total del día.',
  'ayer-nada': 'Ayer no hubo ventas registradas en este negocio.',
  'semana-cobrado': 'Esto es lo que ha entrado en los últimos siete días. Te dejo los números aquí abajo.',
  'semana-nada': 'En los últimos siete días todavía no hay ventas registradas.',
  'resultado-positivo':
    'Este mes vas en positivo: lo que entró cubre tus gastos y todavía te queda. Los números están aquí abajo.',
  'resultado-negativo': 'Este mes tus gastos van por encima de lo que ha entrado. Revisa los números aquí abajo.',
  'resultado-sin-gastos': 'Para decirte cuánto te queda necesito tus gastos del mes. Anótalos en Finanzas y te lo explico.',
  'resultado-nada': 'Este mes todavía no hay movimientos. Cuando cobres o anotes un gasto, te digo cuánto te queda.',

  // Gastos.
  'gastos-mes': 'Estos son los gastos que llevas anotados este mes. El total está aquí abajo.',
  'gastos-nada': 'Este negocio todavía no tiene gastos anotados este mes. Anótalos en Finanzas y te hago las cuentas.',
  'mayor-alquiler': 'Tu gasto más grande este mes es el alquiler.',
  'mayor-planilla': 'Tu gasto más grande este mes es la planilla.',
  'mayor-insumos': 'Tu gasto más grande este mes son los insumos.',
  'mayor-servicios': 'Tu gasto más grande este mes son los servicios.',
  'mayor-afiliacion': 'Tu gasto más grande este mes es tu afiliación a Dale.',
  'mayor-otro': 'Tu gasto más grande este mes está en otros gastos.',
  'planilla-mes': 'Esto es lo que llevas anotado en planilla este mes. El total está aquí abajo.',
  'planilla-nada': 'Este negocio todavía no tiene planilla anotada este mes. Anótala en Finanzas cuando la pagues.',
  'fijos-hay': 'Estos son tus gastos fijos de cada mes. El total está aquí abajo.',
  'fijos-nada': 'Este negocio todavía no tiene gastos fijos anotados. Márcalos como mensuales en Finanzas y los sumo.',

  // Other periods.
  'comparar-sube': 'Vas por encima del mes pasado en los mismos días. Aquí abajo ves la diferencia.',
  'comparar-baja': 'Vas por debajo del mes pasado en los mismos días. Aquí abajo ves la diferencia.',
  'comparar-igual': 'Vas igual que el mes pasado en los mismos días.',
  'comparar-nada': 'Todavía no tengo un mes anterior con qué comparar. Cuando lo tengas, te cuento cómo vas.',
  'mes-pasado': 'Este fue el cierre del mes pasado. Los números están aquí abajo.',
  'mes-pasado-nada': 'El mes pasado no tiene movimientos registrados en este negocio.',
  anio: 'Este es tu año hasta hoy. Los números están aquí abajo.',
  'anio-nada': 'Este año todavía no hay movimientos registrados en este negocio.',

  // Cobros.
  'porcobrar-hay': 'Tienes dinero pendiente por cobrar. El monto está aquí abajo.',
  'porcobrar-cero': 'No tienes nada pendiente por cobrar. Todo está al día.',
  'metodo-efectivo': 'La mayor parte de lo que te pagan llega en efectivo.',
  'metodo-tarjeta': 'La mayor parte de lo que te pagan llega con tarjeta.',
  'metodo-yappy': 'La mayor parte de lo que te pagan llega por Yappy.',
  'metodo-transferencia': 'La mayor parte de lo que te pagan llega por transferencia.',
  'metodo-banco': 'La mayor parte de lo que te pagan llega por Banco Aliado.',
  'metodo-otro': 'La mayor parte de lo que te pagan llega por otros medios.',
  'metodo-nada': 'Este mes todavía no hay cobros registrados para decirte cómo te pagan.',

  // Who and what brings the money.
  'servicio-top': 'Este es el servicio que más te deja este mes. Lo ves aquí abajo, con lo que ha generado.',
  'servicio-nada': 'Este mes todavía no hay servicios cobrados para decirte cuál te deja más.',
  'equipo-top': 'Esta es la persona de tu equipo que más ha generado este mes. La ves aquí abajo.',
  'equipo-nada': 'Este negocio todavía no tiene ventas por persona este mes.',
  'cliente-top': 'Este es el cliente que más te ha comprado este mes. Lo ves aquí abajo.',
  'cliente-nada': 'Este mes todavía no hay clientes con compras registradas.',
  'ticket-hay': 'Esto es lo que deja cada cobro en promedio. El número está aquí abajo.',
  'faltas-hay': 'Este mes perdiste dinero por citas que no llegaron o se cancelaron. Aquí abajo ves cuánto.',
  'faltas-cero': 'Este mes no has perdido dinero por citas que no llegaron ni se cancelaron.',
  'impuesto-hay': 'Este es el ITBMS de las facturas que emitiste este mes. Lo ves aquí abajo.',
  'impuesto-nada': 'Este mes todavía no has emitido facturas, así que no hay impuesto que mostrar.',

  // Wishes and growth: what to do when the owner says what they want, not a question.
  'mejorar-finanzas':
    'Para mejorar tus finanzas, primero hay que ver dónde estás. Te dejo los números del mes; pregúntame por tus gastos o por lo que más te deja.',
  'mas-clientes-hay': 'Para traer más clientes, esto es lo que tu equipo ya preparó. Revísalo y apruébalo tú.',
  'mas-clientes-nada': 'Ahora mismo no veo un día flojo ni clientes por recuperar. Cuando eso cambie, te preparo algo aquí mismo.',

  // The owner reacts to the work that was prepared.
  'propuesta-rechazar': 'Entendido. Nada sale sin tu aprobación: descártala cuando quieras, o ábrela y cámbiale lo que no te guste.',
  'propuesta-aprobar': 'Me alegra que te guste. Ábrela, revisa los detalles y apruébala tú: nada sale sin tu aprobación.',
  'propuesta-cambiar':
    'Ábrela y revísala: puedes cambiarle el texto, el descuento o las fechas, aprobarla o descartarla. Nada sale sin tu aprobación.',
  'propuesta-porque': 'Te la propuse por lo que ves aquí abajo. Si no te convence, ábrela y cámbiala, o descártala.',
  'propuesta-otra': 'Por ahora esa es la que tengo lista. Si no te convence, descártala, y cuando vea otra oportunidad te preparo una nueva.',
  'propuesta-despues': 'Sin problema. Se queda en tu lista hasta que la revises.',
  'propuesta-nada': 'Ahora mismo no tengo nada pendiente para que revises. Cuando prepare algo nuevo, te aviso aquí.',

  // Who we are, and the small talk around the work.
  'quien-eres': 'Soy Júpiter, el asistente de inteligencia artificial de Dale Agents. Mi equipo y yo preparamos el trabajo; tú lo revisas y lo apruebas.',
  'de-nada': 'Con gusto. Aquí estoy si necesitas algo más.',
  'hasta-luego': 'Hasta luego. Tu equipo sigue aquí cuando lo necesites.',
  'gracias-elogio': 'Gracias, qué bueno que te sirva. Seguimos cuidando tu negocio.',
  'perdon-queja': 'Perdona, todavía estoy aprendiendo. Pregúntame por tus ventas, tus gastos, tus clientes o tus citas, y lo intento de nuevo.',
  'no-hago': 'Eso todavía no lo hago por ti. Yo preparo el trabajo; tú lo revisas, lo apruebas y lo envías.',

  // When the answer is not there.
  'sin-info': 'Este negocio todavía no tiene esa información. En cuanto la tenga, te la cuento aquí mismo.',
  'sin-permiso': 'Esa parte la ve solo quien lleva las finanzas del negocio.',
  'error-cuentas': 'No pude sacar las cuentas ahora mismo. Intenta de nuevo en un momento.',
} as const;

export type LineId = keyof typeof LINES;

export const LINE_IDS = Object.keys(LINES) as LineId[];

export function lineText(id: LineId): string {
  return LINES[id];
}
